import { useRef, useMemo, useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Row } from "react-table";
import { DateTime } from 'luxon';
import DataTable from "../../../DataTable/datatable.component";
import DateColumn from "../../../DataTable/datatable.datecolumn";
import QirColumn from '../../../DataTable/datatable.qircolumn';
import SelectFilter from "../../../DataTable/datatable.selectfilter";
import { Package } from "../../../Types/Package";
import PackageNumberColumn from "../../LotDetails/LotDetailsComponents/PackagesSection/PackageNumberColumn";
import { QIR } from '../../../Types/QIR';
import { convertToDateTime } from "../../../Utilities/useDateUtils";

interface LotSplitPackageDialogTableProps {
    firstLotNumber: string,
    secondLotNumber: string,
    packages: Package[],
    qirs: QIR[],
    filteredPackagesNumbers: string[],
    setFilteredPackages: (rows: Row<Package>[]) => void,
    changePackageLot: (containerNumbers: string[], lotChecked: string) => void,
    selectAllPackages: (lot: string) => void,
    splitTimeIsAFilter?: boolean,
    fromDateTime?: DateTime | null,
    toDateTime?: DateTime | null
};

interface DateRangeFilter {
    fromDateTime?: DateTime | null
    toDateTime?: DateTime | null
};

type LotColumn = "A" | "B"; // Where "A" represents the source lot and "B" is the destination lot

function SelectAllPackagesButtonGenerator(selectPackagesCallback: (lot: string) => void, type: string) {

    const SelectAllPackagesButton = ({ column }: { column: { filteredRows: Row<{lot: string}>[]} }) => {

        const checked = column.filteredRows.every(r => r.original.lot === type);

        return (
            <Form.Check
                checked={checked}
                onChange={(e) => {
                    if (!checked) {
                        selectPackagesCallback(type);
                    }
                }}
            />
        );
    }

    return SelectAllPackagesButton;
}

function LotSplitPackageDialogTable({ firstLotNumber, secondLotNumber, packages, qirs, filteredPackagesNumbers, setFilteredPackages, changePackageLot, selectAllPackages, splitTimeIsAFilter, fromDateTime, toDateTime }: LotSplitPackageDialogTableProps) {
    const { t } = useTranslation();

    const lastCheckedARef = useRef<string | null>(null);
    const lastCheckedBRef = useRef<string | null>(null);

    const rangeBetweenFilteredPackages = (a: string, b: string) => {

        const aIndex = filteredPackagesNumbers.findIndex(p => p === a);
        const bIndex = filteredPackagesNumbers.findIndex(p => p === b);

        if (aIndex === -1 || bIndex === -1) {
            return [];
        }

        const start = Math.min(aIndex, bIndex);
        const end = Math.max(aIndex, bIndex);

        return filteredPackagesNumbers.slice(start, end+1);
    }

    const handleChange = (shiftKey: any, containerNumber: string, lotColumn: LotColumn) => {
        var lastCheckedRef;
        if (lotColumn === "A") {
            lastCheckedRef = lastCheckedARef;
        } else if (lotColumn === "B") {
            lastCheckedRef = lastCheckedBRef;
        } else {
            throw new Error(`LotColumn ${lotColumn} does not exist`);
        }

        if (shiftKey && lastCheckedRef.current !== null) {
            let selectedPackages = rangeBetweenFilteredPackages(containerNumber, lastCheckedRef.current);
            changePackageLot(selectedPackages, lotColumn);
            
        } else {
            changePackageLot([containerNumber], lotColumn);
        }

        lastCheckedRef.current = containerNumber;
    };

    const SelectAFilterButton = useMemo(() => SelectAllPackagesButtonGenerator(selectAllPackages, "A"), [selectAllPackages])
    const SelectBFilterButton = useMemo(() => SelectAllPackagesButtonGenerator(selectAllPackages, "B"), [selectAllPackages])

    const getCellProps = (cell: any) => {
        return { className: "align-middle" };
    };

    const [dateRange, setDateRange] = useState<DateRangeFilter | null>(null);

    const filterInDateRange = (rows: Row<Package>[], id: any, filterValue: DateRangeFilter) => {

        if (splitTimeIsAFilter) {
            return rows.filter(row => {
                const rowValue = convertToDateTime(row.values[id])

                if (filterValue?.fromDateTime != null && rowValue != null && rowValue < filterValue.fromDateTime) {
                    return false;
                }

                if (filterValue?.toDateTime != null && rowValue != null && rowValue > filterValue.toDateTime) {
                    return false;
                }

                return true;
            })
        } else {
            return rows;
        }
    };
        
    useEffect(() => {
        setDateRange({ fromDateTime: fromDateTime, toDateTime: toDateTime });
    }, [fromDateTime, toDateTime]);

    const DateRangeFilter = ({ column }: any) => {

        var filterValue = column.filterValue as DateRangeFilter | undefined | null;

        if (filterValue) {
            if (filterValue?.fromDateTime != dateRange?.fromDateTime || filterValue?.toDateTime != dateRange?.toDateTime) {
                column.setFilter(dateRange);
            }
        } else {
            column.setFilter(dateRange);
        }

        return (
            <></>
        )
    };

    const tableColumns = [
        PackageNumberColumn({ width: "10%" }),
        {
            ...DateColumn({ header: t('LotDetails.Packages.PackageDateTime'), accessor: 'packageDateTimeUtc', width: "10%" }),
            disableFilters: false,
            filter: filterInDateRange,
            Filter: DateRangeFilter
        },
        {
            Header: t<string>('LotDetails.Packages.Status'),
            accessor: 'status',
            filter: 'equals',
            Filter: SelectFilter,
        },
        {
            Header: t<string>('LotDetails.Packages.Grade'),
            accessor: 'grade',
            filter: 'equals',
            Filter: SelectFilter,
        },
        {
            Header: t<string>('LotDetails.Packages.PackageType'),
            accessor: 'packageType',
            filter: 'equals',
            Filter: SelectFilter,
        },
        {
            Header: t<string>('LotDetails.Packages.TareWeight'),
            accessor: 'tareWeight',
            filter: 'includesString',
        },
        {
            Header: t<string>('LotDetails.Packages.NetWeight'),
            accessor: 'netWeight',
            filter: 'includesString',
        },
        {
            Header: t<string>('LotDetails.Packages.SiteCode'),
            accessor: 'siteCode',
            filter: 'equals',
            Filter: SelectFilter
        },
        {
            Header: t<string>('LotDetails.Packages.Scale'),
            accessor: 'scale',
            filter: 'equals',
            Filter: SelectFilter
        },
        {
            Header: t<string>('LotDetails.Packages.RedDot'),
            accessor: 'redDotCode',
            filter: 'equals',
            Filter: SelectFilter
        },
        {
            Header: t<string>('LotDetails.Packages.Notes'),
            accessor: 'notes',
            filter: 'includesString'
        },
        QirColumn({ header: t<string>('LotSplit.QIRs'), accessor: 'qirIncidentIds', qirs }),
        {
            id: "colA",
            Header: firstLotNumber,
            accessor: 'lot',
            Cell: ({ row }: any) => (
                <Form.Check
                    disabled={firstLotNumber === secondLotNumber}
                    data-index={row.id}
                    checked={row.original.lot === "A"}
                    onChange={(e) => handleChange((e.nativeEvent as PointerEvent).shiftKey, row.original.containerNumber, "A")} />
            ),
            Filter: SelectAFilterButton
        },
        {
            id: "colB",
            Header: secondLotNumber,
            accessor: 'lot',
            Cell: ({ row }: any) => (
                <Form.Check
                    disabled={firstLotNumber === secondLotNumber}
                    data-index={row.id}
                    checked={row.original.lot === "B"}
                    onChange={(e) => handleChange((e.nativeEvent as PointerEvent).shiftKey, row.original.containerNumber, "B")} />
            ),
            Filter: SelectBFilterButton
        }
    ];

    const initialState = {
        sortBy: [
            {
                id: 'packageDateTimeUtc',
                desc: false
            }
        ],
        filters: [
            {
                id: 'packageDateTimeUtc',
                value: { from: fromDateTime, to: toDateTime }
            }
        ]
    };

    return (
        <DataTable tableData={packages} tableColumns={tableColumns} getCellProps={getCellProps} showPaging={false} initialState={initialState} setFilteredRows={setFilteredPackages} />
    );
}

export default LotSplitPackageDialogTable;